import { useCallback, useMemo, useState } from "react";
import { type CellData, formatCellValue } from "./excel-parse";

export type SortDirection = "asc" | "desc";

export interface SheetSort {
  colIndex: number;
  direction: SortDirection;
}

export interface SheetFilterSortReturn {
  /** Rows to render, after freezing the header row, filtering, and sorting. */
  displayData: CellData[][];
  /** 1-based physical sheet row for each `displayData` entry. */
  displayRowNumbers: number[];
  /** Row heights aligned to `displayData`. */
  displayRowHeights: number[];
  /** Original `sheetData` index for each `displayData` entry (for row resize). */
  displayOrigIndex: number[];
  /** Active single-column sort, or null. */
  sort: SheetSort | null;
  /** Set/replace the sort for a column; passing null clears it. */
  setSort: (colIndex: number, direction: SortDirection | null) => void;
  /** Distinct display values available to filter in a column (data rows only). */
  getColumnValues: (colIndex: number) => string[];
  /** Selected values for a column, or null when the column is unfiltered. */
  getSelectedValues: (colIndex: number) => Set<string> | null;
  /** Replace the selected-value set for a column; null clears the filter. */
  setColumnFilter: (colIndex: number, selected: Set<string> | null) => void;
  isColumnFiltered: (colIndex: number) => boolean;
  isColumnSorted: (colIndex: number) => boolean;
  /** Clear every column filter and the sort. */
  clearAll: () => void;
  /** True when any filter or sort is active. */
  anyActive: boolean;
}

/**
 * Excel-style per-column sort + value filter over a parsed sheet.
 *
 * The FIRST rendered row is treated as a frozen header (matching Excel's
 * AutoFilter): it is never filtered out and never sorted, so the column labels
 * stay pinned at the top. All remaining rows are the filterable/sortable body.
 *
 * Rows keep their identity: `displayRowNumbers` / `displayOrigIndex` follow the
 * reordered/filtered rows so the physical-row highlight (STK-1299) and row
 * resize keep pointing at the right row. Cell selection is driven off
 * `displayData`, so it stays consistent with what is on screen.
 */
export function useSheetFilterSort(
  sheetData: CellData[][],
  rowNumbers: number[],
  rowHeights: number[],
  /**
   * Changing this clears every filter and the sort. Pass a value that differs
   * whenever the rendered content swaps (active sheet, file↔slice buffer) so a
   * filter set on one view never silently empties another (which reads as a
   * blank grid with no explanation).
   */
  resetKey?: string,
): SheetFilterSortReturn {
  const [sort, setSortState] = useState<SheetSort | null>(null);
  // colIndex -> selected display values. Absent key = column unfiltered.
  const [filters, setFilters] = useState<Map<number, Set<string>>>(
    () => new Map(),
  );

  // Reset synchronously during render (not in an effect) so the swapped-in view
  // never renders one frame filtered by the previous view's selection. This is
  // React's "adjust state when a prop changes" pattern — the previous key lives
  // in state (not a ref) so the reset stays render-pure and deterministic under
  // StrictMode / concurrent render retries.
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    if (sort !== null) setSortState(null);
    if (filters.size > 0) setFilters(new Map());
  }

  const hasHeader = sheetData.length > 1;
  const headerCount = hasHeader ? 1 : 0;

  // Distinct-value lists are derived once per column and cached until the sheet
  // data (or header offset) changes. Without this, every header renders its menu
  // by rescanning all body rows on every parent render — O(cols × rows) per frame
  // on large sheets.
  const columnValuesCache = useMemo(
    () => new Map<number, string[]>(),
    // Deps are cache-invalidation keys: a fresh Map when the data/header
    // changes. They are intentionally unused in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sheetData, headerCount],
  );
  const getColumnValues = useCallback(
    (colIndex: number) => {
      const cached = columnValuesCache.get(colIndex);
      if (cached) return cached;
      const seen = new Set<string>();
      const values: string[] = [];
      for (let r = headerCount; r < sheetData.length; r++) {
        const v = formatCellValue(sheetData[r]?.[colIndex]);
        if (!seen.has(v)) {
          seen.add(v);
          values.push(v);
        }
      }
      columnValuesCache.set(colIndex, values);
      return values;
    },
    [sheetData, headerCount, columnValuesCache],
  );

  const setSort = useCallback(
    (colIndex: number, direction: SortDirection | null) => {
      setSortState(direction ? { colIndex, direction } : null);
    },
    [],
  );

  const setColumnFilter = useCallback(
    (colIndex: number, selected: Set<string> | null) => {
      setFilters((prev) => {
        const next = new Map(prev);
        if (!selected) {
          next.delete(colIndex);
        } else {
          next.set(colIndex, selected);
        }
        return next;
      });
    },
    [],
  );

  const clearAll = useCallback(() => {
    setSortState(null);
    setFilters(new Map());
  }, []);

  const isColumnFiltered = useCallback(
    (colIndex: number) => filters.has(colIndex),
    [filters],
  );
  const isColumnSorted = useCallback(
    (colIndex: number) => sort?.colIndex === colIndex,
    [sort],
  );

  const {
    displayData,
    displayRowNumbers,
    displayRowHeights,
    displayOrigIndex,
  } = useMemo(() => {
    // Body rows (everything after the frozen header) as original indices.
    const bodyIdx: number[] = [];
    for (let r = headerCount; r < sheetData.length; r++) bodyIdx.push(r);

    // Filter: a row survives only when each filtered column's cell value is in
    // that column's selected set.
    const filtered = bodyIdx.filter((r) => {
      for (const [colIndex, selected] of filters) {
        const v = formatCellValue(sheetData[r]?.[colIndex]);
        if (!selected.has(v)) return false;
      }
      return true;
    });

    // Sort (stable): numeric compare when both cells are numeric, else a
    // locale-aware string compare on the formatted value.
    if (sort) {
      const { colIndex, direction } = sort;
      const dir = direction === "asc" ? 1 : -1;
      const decorated = filtered.map((r, i) => ({ r, i }));
      decorated.sort((a, b) => {
        const ca = sheetData[a.r]?.[colIndex];
        const cb = sheetData[b.r]?.[colIndex];
        const na = ca?.numericValue;
        const nb = cb?.numericValue;
        let cmp: number;
        if (typeof na === "number" && typeof nb === "number") {
          cmp = na - nb;
        } else {
          cmp = formatCellValue(ca).localeCompare(
            formatCellValue(cb),
            undefined,
            {
              numeric: true,
              sensitivity: "base",
            },
          );
        }
        return cmp !== 0 ? cmp * dir : a.i - b.i;
      });
      filtered.length = 0;
      filtered.push(...decorated.map((d) => d.r));
    }

    const order = [
      ...Array.from({ length: headerCount }, (_, i) => i),
      ...filtered,
    ];
    return {
      displayData: order.map((r) => sheetData[r]),
      displayRowNumbers: order.map((r) => rowNumbers[r]),
      displayRowHeights: order.map((r) => rowHeights[r]),
      displayOrigIndex: order,
    };
  }, [sheetData, rowNumbers, rowHeights, filters, sort, headerCount]);

  return {
    displayData,
    displayRowNumbers,
    displayRowHeights,
    displayOrigIndex,
    sort,
    setSort,
    getColumnValues,
    getSelectedValues: useCallback(
      (colIndex: number) => filters.get(colIndex) ?? null,
      [filters],
    ),
    setColumnFilter,
    isColumnFiltered,
    isColumnSorted,
    clearAll,
    anyActive: sort !== null || filters.size > 0,
  };
}
